// Shared contracts + pure payload mappers for the unified lot editor
// (LotEditModal). Every function here is pure form-data ↔ API-body shape work with
// no React, no I/O and no side effects, so it is unit-testable on its own and
// shared by BOTH doors: the edit body (LotEditBody.tsx) and the create body
// (LotCreateBody.tsx).
//
// The cross-module type `LotEditTab` lives here too — it is the contract the
// modal and its header agree on, and this is the one module both already import.
// (`LotCreatedResult` lived here until BL-1450 retired the BL-1058 post-create
// hand-off; nothing reports a created record's id up any more.)

import { isLandCategory } from '@auctogon/core-admin/item-form';
import type { LotCreateRequestPayload } from '@auctogon/core-admin/schemas/item-form';
import type { InventoryLotContactDefaultsPayload } from '@auctogon/core-admin/schemas/responses/inventory-lot-contact-defaults';
import type { StagedDocumentRef, StagedMediaRef } from '@auctogon/core-admin/schemas';
import type { ItemFormData } from '../shared/ItemForm';
import type { MediaItem } from '../inventory/InventoryMediaManager';
import type { ExternalDocument } from '@auctogon/admin-isomorphic/types/inventory';
import type { LotDetailPayload } from '@auctogon/core-admin/schemas/responses/lot-detail';
import { fromWire, type MoneyWire } from '@auctogon/core/money';
import { moneyWireForInput } from '@auctogon/admin-isomorphic/isomorphic/lots/edit-lots';

/** Which pane is active. `details`/`lot-history`/`bids` exist only for a lotted lot. */
// BL-1978: the `lot` tab id is deleted — Details holds every lot field now.
export type LotEditTab = 'details' | 'lot-history' | 'bids';

/**
 * BL-2243: a detail-row price (a `MoneyWire` in the lot's own currency) as the
 * form's MoneyWire — the same amount and currency, at the scale the form holds a
 * typed one. Null stays null ("none", never $0).
 */
function seedWire(wire: MoneyWire | null | undefined): MoneyWire | null {
  return wire == null ? null : moneyWireForInput(wire);
}

/**
 * BL-2444 — the Starting Price seed: a stored zero seeds an empty box, any
 * other price seeds as the operator typed it.
 *
 * @param wire - the lot's stored Starting Price
 * @returns the form's seed (`null` = empty)
 */
function seedStartPrice(wire: MoneyWire | null | undefined): MoneyWire | null {
  const seed = seedWire(wire);
  return seed == null || fromWire(seed).isZero() ? null : seed;
}

/** A zero amount in `currency`, as the form's typed wire spells it. */
function zeroWire(currency: string): MoneyWire {
  return { amount: '0', currency };
}

/**
 * Seeds the shared form from the lot's authoritative detail row. Every field the
 * unified editor renders is sourced here; ItemForm's getInitialFormData fills the
 * rest.
 *
 * BL-2243: the prices are seeded straight from the detail row's `MoneyWire`s,
 * each in the lot's own currency (the payload adds no separate `currency` key).
 * This is the one-shot form seed BL-1299 permits for the start price and
 * reserve the lot-state stream also projects (rule #32): read once on open,
 * never rendered beside the stream, and saved as an explicit operator override.
 * A newly TYPED price still takes the record tenant's currency (BL-2131), and
 * the editor holds Edit until that currency resolves.
 *
 * @param lot - the lot's detail row
 * @returns the form's seed
 */
export function lotDetailToInitialData(lot: LotDetailPayload): Partial<ItemFormData> {
  return {
    name: lot.name ?? '',
    description: lot.description ?? '',
    categoryId: lot.categoryId ?? '',
    make: lot.make ?? '',
    model: lot.model ?? '',
    year: lot.year != null ? String(lot.year) : '',
    vin: lot.vin ?? '',
    serialNumber: lot.serialNumber ?? '',
    hoursMileage: lot.hoursMileage ?? null,
    mileageUnit: lot.mileageUnit === 'km' ? 'km' : 'miles',
    // BL-2444 AC 4: a stored $0 is "open at the first bid step" — the box shows
    // empty and its placeholder names that opening bid. An empty box saves $0
    // again (formDataToUpdatedItem). The column stays NOT NULL DEFAULT 0.
    startPrice: seedStartPrice(lot.startPrice),
    reserve: seedWire(lot.reserve),
    sellerId: lot.sellerId ?? '',
    // BL-1991: the seller's identity record seeds the picker's closed field.
    sellerIdentity: lot.sellerIdentity ?? null,
    // LotDetailPayload carries no seller NUMBER (display-only on the picker).
    sellerNumber: '',
    // BL-1449: the seller's own rep, seeded from the lot's detail row. The create
    // door gets the same two values from the seller picker instead — one form
    // field, two sources, so the Sales Rep control behaves identically on both.
    sellerSalesRepId: lot.sellerSalesRepId ?? null,
    sellerSalesRepName: lot.sellerSalesRepName ?? null,
    auctionId: lot.auctionId ?? '',
    auctionName: lot.auctionName ?? '',
    isTitledItem: lot.isTitledItem ?? false,
    titleStatus: lot.titleStatus ?? '',
    titleState: lot.titleState ?? '',
    // Title mailing (BUG-1091) — lot-held, so it seeds straight off the detail.
    titleMailingStatus: lot.titleMailingStatus ?? '',
    titleMailingTrackingNumber: lot.titleMailingTrackingNumber ?? '',
    titleMailingDateSent: lot.titleMailingDateSent ?? '',
    titleMailingRecipientName: lot.titleMailingRecipientName ?? '',
    titleMailingRecipientAddress: lot.titleMailingRecipientAddress ?? '',
    acreage: lot.acreage ?? null,
    // BUG-2397: an unset (NULL) pricing type is whole price, as `isPerAcrePricing`
    // and every other surface read it — only a stored 'per_acre' seeds per acre.
    pricingType: lot.pricingType === 'per_acre' ? 'per_acre' : 'total',
    // BL-1767: the lot's tax pair seeds the Tax section.
    taxStatus: lot.taxStatus,
    taxRateId: lot.taxRate?.id ?? null,
    salesRepId: lot.salesRepId ?? '',
    // BL-2748: seed the contact slots from the lot, so an open-and-save cannot
    // write the form's defaults over them. A cached offline detail from before
    // the slots existed reads as the defaults (rep on, the rest off).
    showSalesRep: lot.showSalesRep ?? true,
    showSeller: lot.showSeller ?? false,
    showAdditionalContact: lot.showAdditionalContact ?? false,
    additionalContactTitle: lot.additionalContactTitle ?? null,
    additionalContactName: lot.additionalContactName ?? null,
    additionalContactPhone: lot.additionalContactPhone ?? null,
    additionalContactEmail: lot.additionalContactEmail ?? null,
    locationId: lot.locationId ?? '',
    hasLoadOutFee: lot.hasLoadOutFee ?? false,
    loadOutFee: seedWire(lot.loadOutFee),
    visibleToSeller: lot.visibleToSeller ?? false,
    media: (lot.media as MediaItem[] | undefined) ?? [],
    // BL-1231: seed the custom-field values so the editor's inputs render them.
    customFields: lot.customFields ?? {},
  };
}

/**
 * Maps the submitted form payload into the camelCase body for the unified
 * `PATCH /api/lots/[id]` (its dual-key builder accepts the `name` / `startPrice` /
 * `reserve` dialect used here). BL-1053: `status` is no longer a save-time field —
 * lot status transitions go through the header status dropdown (the batch engine),
 * so the lot PATCH never carries `status`.
 */
export function formDataToUpdatedItem(data: ItemFormData, lot: LotDetailPayload) {
  const isLand = isLandCategory(data.categoryId);
  return {
    id: lot.id,
    name: data.name,
    description: data.description,
    categoryId: data.categoryId,
    make: data.make,
    model: data.model,
    year: data.year ? Number(data.year) : null,
    vin: data.vin,
    serialNumber: data.serialNumber,
    hoursMileage: data.hoursMileage,
    mileageUnit: data.mileageUnit,
    // BL-2444 AC 3: the PATCH refuses a null Starting Price (the column is NOT
    // NULL), so an empty box saves a zero in the lot's own currency.
    startPrice: data.startPrice ?? zeroWire(lot.startPrice.currency),
    reserve: data.reserve,
    sellerId: data.sellerId,
    taxStatus: data.taxStatus,
    taxRateId: data.taxRateId,
    isTitledItem: data.isTitledItem,
    titleStatus: data.titleStatus,
    titleState: data.titleState,
    // Title mailing (BUG-1091) — rides the same lot PATCH as the fields above.
    titleMailingStatus: data.titleMailingStatus,
    titleMailingTrackingNumber: data.titleMailingTrackingNumber,
    titleMailingDateSent: data.titleMailingDateSent,
    titleMailingRecipientName: data.titleMailingRecipientName,
    titleMailingRecipientAddress: data.titleMailingRecipientAddress,
    hasLoadOutFee: data.hasLoadOutFee,
    loadOutFee: data.loadOutFee,
    visibleToSeller: data.visibleToSeller,
    salesRepId: data.salesRepId,
    showSalesRep: data.showSalesRep,
    showSeller: data.showSeller,
    showAdditionalContact: data.showAdditionalContact,
    additionalContactTitle: data.additionalContactTitle,
    additionalContactName: data.additionalContactName,
    additionalContactPhone: data.additionalContactPhone,
    additionalContactEmail: data.additionalContactEmail,
    locationId: data.locationId,
    acreage: isLand ? data.acreage : null,
    pricingType: isLand ? data.pricingType : null,
    // BL-2348: no `media` — photos ride the Save as a per-item change list
    // (`buildLotAttachmentChanges`, in `saveLotEdit`), never as a whole array.
    // BL-1231: only present when the tenant has enabled custom fields (ItemForm
    // merges it conditionally); the lot PATCH validates + persists it.
    ...(data.customFields ? { customFields: data.customFields } : {}),
    // BL-1070: the BL-1065 inline associate-from-edit was removed — the inventory
    // form no longer carries an auction picker, so a home lot is assigned to an
    // auction through the explicit "Assign auction" move action, not this save.
  };
}


/**
 * The Save body the form would send for `lot` with nothing edited: the baseline an offline Save is
 * compared with, so only the fields the operator changed are queued (BL-2431). Built by the same two
 * mappers the form uses, so a field reads the same way on both sides of the comparison.
 *
 * @param lot - the lot's detail as the edit began.
 * @returns the unedited Save body.
 */
export function lotPatchBaseline(lot: LotDetailPayload): ReturnType<typeof formDataToUpdatedItem> {
  // The seed holds every field formDataToUpdatedItem reads; ItemForm's own defaults only fill
  // fields the Save never sends (the seller picker's display fields).
  return formDataToUpdatedItem(lotDetailToInitialData(lot) as ItemFormData, lot);
}

/** Empty and null text slots are the same "nothing" to the Contacts section and to the server's defaults. */
function slotText(value: string | null | undefined): string {
  return value ?? '';
}

/**
 * The seven contact slots a create should carry: all of them when the Contacts section differs from the house
 * defaults it opened on, none when it is untouched or the defaults never loaded (BUG-2635).
 *
 * @param data - The Add form's values.
 * @param defaults - The house's Default lot contacts the section was seeded from, or null.
 * @returns The slot keys to spread into the payload (possibly empty).
 */
function contactSlotsForCreate(
  data: ItemFormData,
  defaults: InventoryLotContactDefaultsPayload | null,
): Partial<LotCreateRequestPayload> {
  if (defaults == null) return {};
  const unchanged =
    data.showSalesRep === defaults.showSalesRep &&
    data.showSeller === defaults.showSeller &&
    data.showAdditionalContact === defaults.showAdditionalContact &&
    slotText(data.additionalContactTitle) === slotText(defaults.additionalContactTitle) &&
    slotText(data.additionalContactName) === slotText(defaults.additionalContactName) &&
    slotText(data.additionalContactPhone) === slotText(defaults.additionalContactPhone) &&
    slotText(data.additionalContactEmail) === slotText(defaults.additionalContactEmail);
  if (unchanged) return {};
  return {
    showSalesRep: data.showSalesRep,
    showSeller: data.showSeller,
    showAdditionalContact: data.showAdditionalContact,
    additionalContactTitle: data.additionalContactTitle,
    additionalContactName: data.additionalContactName,
    additionalContactPhone: data.additionalContactPhone,
    additionalContactEmail: data.additionalContactEmail,
  };
}

/**
 * The ONE create payload, shared by both doors (BL-1449).
 *
 * Was two mappers — `formDataToCreateInput` (inventory) and
 * `formDataToCreateLotBody` (auction) — field-for-field identical apart from
 * `pricingType`'s non-land value, and BOTH omitting the five Edit-only field
 * groups. Two copies is why per-door divergence was possible at all (AC 3); they
 * are collapsed here, and the doors now differ only in which endpoint
 * `performLotCreate` posts to.
 *
 * The shape itself is `LotCreateRequestPayload` from `@auctogon/core-admin` — the
 * single declaration, sitting beside the two Zod schemas that gate it on the
 * server. This function is only the ItemFormData → that-shape mapping.
 *
 * Deliberately a MINIMAL passthrough of the operator's entered fields — every
 * default and every step of the orchestration is the SERVER's (ADR 026). The
 * auction door's route mints the inventory item, lands the lot in the Unassigned
 * bucket (a client-supplied `ringId` is ignored by design), defaults `increment`
 * → 0, `status` → `'draft'`, and derives the auction seller roster from
 * `seller_id` (BL-1013); the inventory door's service auto-allocates the
 * inventory number. None of those are sent — the client never re-implements a
 * server default.
 */
export function formDataToCreatePayload(
  data: ItemFormData,
  options: { contactDefaults?: InventoryLotContactDefaultsPayload | null } = {},
): LotCreateRequestPayload {
  // BUG-1085: acreage + per-acre pricing only apply to land. ItemForm seeds
  // `pricingType: 'per_acre'` as its default and only resets it once a non-land
  // category is picked, so a non-land / no-category item still carries the stale
  // default — which the lots route rejects with "Acreage is required and must be
  // positive for per-acre pricing". Null the land fields here (mirrors
  // `formDataToUpdatedItem`) so a categoryless item never trips that validation.
  const isLand = isLandCategory(data.categoryId);
  return {
    name: data.name,
    description: data.description,
    categoryId: data.categoryId,
    make: data.make || undefined,
    model: data.model || undefined,
    year: data.year ? Number(data.year) : undefined,
    vin: data.vin || undefined,
    serialNumber: data.serialNumber || undefined,
    hoursMileage: data.hoursMileage ?? undefined,
    mileageUnit: data.mileageUnit || undefined,
    startPrice: data.startPrice,
    reserve: data.reserve,
    sellerId: data.sellerId || undefined,
    isTitledItem: data.isTitledItem,
    titleStatus: data.titleStatus || undefined,
    acreage: isLand ? data.acreage : null,
    pricingType: isLand ? data.pricingType : null,
    taxStatus: data.taxStatus,
    taxRateId: data.taxRateId,
    // BL-1449: the five field groups the Add door used to gate off. They ride the
    // SAME payload on both doors, which is what AC 3 asserts.
    titleState: data.titleState,
    salesRepId: data.salesRepId,
    // BUG-2635: the Contacts section's slots ride a create only when the operator changed them from the
    // house defaults the section opened on. An untouched section sends none, so the server stamps the same
    // defaults and a house default changed meanwhile is not overridden by a stale copy.
    ...contactSlotsForCreate(data, options.contactDefaults ?? null),
    locationId: data.locationId,
    hasLoadOutFee: data.hasLoadOutFee,
    loadOutFee: data.loadOutFee,
    // Conditional, mirroring `formDataToUpdatedItem`: on a tenant with no custom
    // fields configured the key must be ABSENT, not present-and-undefined, so the
    // server validator never runs against an empty config (AC 4).
    ...(data.customFields ? { customFields: data.customFields } : {}),
    // BL-1450: photos the operator staged on the Add form. Same conditional
    // shape and the same reason — the key is ABSENT on the common photo-less
    // create, which keeps that path out of the staged-attach flow entirely
    // (no pre-generated record id, no storage round-trip).
    ...(data.media?.length ? { stagedMedia: mediaItemsToStagedRefs(data.media) } : {}),
  };
}

/**
 * Reduce the Add form's deferred documents to the staged refs a create carries
 * (BL-1451).
 *
 * Deliberately LOSSY. The deferred array holds the shape the Documents list
 * renders — including the draft `url` and the `att-<hash>` id the upload echoed
 * back. Neither may reach the server: it mints both from the canonical path it
 * copies the object to, precisely so a client cannot pre-declare a document id
 * or point a document's link anywhere it likes (`staged-upload.ts`). Sending
 * them would be silently ignored at best and a smuggling vector at worst.
 *
 * A document with no `storagePath` is dropped rather than sent as `undefined`:
 * the server's `storagePathSchema` would reject the whole create over one
 * malformed entry, losing the operator's other files with it. In practice every
 * staged upload has one — the passthrough upload-complete always returns it.
 *
 * @param docs the deferred documents held by `useLotExternalDocuments`
 * @returns one staged ref per document, in the operator's order
 */
export function toStagedDocumentRefs(docs: ExternalDocument[]): StagedDocumentRef[] {
  return docs.flatMap((doc) =>
    doc.storagePath
      ? [{ storagePath: doc.storagePath, name: doc.name, type: doc.type ?? 'other' }]
      : [],
  );
}

/**
 * Maps the Add form's gallery state onto the create request's staged refs.
 *
 * Carries ONLY what the client can legitimately know before the record exists:
 * where the bytes are, plus how the operator arranged them. Everything
 * identifying — `id`, `url`, `uploadedAt`, the canonical `storagePath` — is
 * minted server-side during the copy, so a client cannot pre-declare a media id
 * or point an entry's url anywhere (BL-1436's `StagedMediaRefSchema` is the
 * shape this must satisfy).
 *
 * A `MediaItem` whose `storagePath` is absent is emitted with an empty path
 * rather than dropped. Every staged upload carries one off `upload-complete`, so
 * this is unreachable in practice — but silently discarding a photo the operator
 * just watched upload is the worst available failure mode (the create succeeds
 * and the file is swept a week later). An empty path fails `storagePathSchema`
 * server-side, which returns a `fieldErrors.stagedMedia` naming the position and
 * lands inline via `applyServerErrors`.
 *
 * NOTE — this deliberately differs from `toStagedDocumentRefs` above, which
 * DROPS a path-less entry to avoid failing the whole create over one malformed
 * document. Both edge cases are unreachable today (every staged upload returns a
 * path), so the divergence costs nothing in practice, but the two policies are
 * genuinely opposed and only one can be right. Flagged for a follow-up decision
 * rather than silently unified here: changing the documents half would alter
 * behavior BL-1451 shipped two days ago, which is a call to make deliberately,
 * not a merge-conflict resolution.
 */
function mediaItemsToStagedRefs(items: MediaItem[]): StagedMediaRef[] {
  return items.map((item) => ({
    storagePath: item.storagePath ?? '',
    type: item.type,
    sortOrder: item.sortOrder,
    isCover: item.isCover,
    // Omit rather than send `undefined` — `caption` is `.optional()` on the ref
    // schema, and an explicit undefined would serialize the key as absent anyway
    // while making the emitted object shape vary by input.
    ...(item.caption ? { caption: item.caption } : {}),
    // BL-2439: a staged video's poster frame, copied beside it by the create.
    ...(item.posterStoragePath ? { posterStoragePath: item.posterStoragePath } : {}),
  }));
}

/**
 * Best-effort post-save list refresh. The write has ALREADY committed by the time
 * this runs, so a failed refetch must NOT be reported as a save failure — that
 * reopens the form and invites a duplicate write (BL-11 lineage). Swallow it to a
 * log and let the save resolve as success.
 */
export async function refreshAfterSave(onSaved?: () => void | Promise<void>): Promise<void> {
  try {
    await Promise.resolve(onSaved?.());
  } catch (refetchErr) {
    // eslint-disable-next-line no-console -- best-effort client log of a non-fatal post-save list refetch failure (the write already committed)
    console.error('Lot saved but the list refresh failed', refetchErr);
  }
}
